import { transformGraphJsonForPreview } from "@airange/shared/topology-x6-preview";
import { Graph, Shape } from "@antv/x6";
import { useEffect, useMemo, useRef } from "react";
import {
  buildNodeLabel,
  buildReadonlyTopologyGraphJsonViewerContainerStyle,
  ensurePreviewNodeRegistry,
  fitReadonlyTopologyGraph,
  getAssetApiBaseUrl,
  READONLY_TOPOLOGY_X6_LANE_HEIGHT as LANE_HEIGHT,
  READONLY_TOPOLOGY_X6_NODE_HEIGHT as NODE_HEIGHT,
  READONLY_TOPOLOGY_X6_NODE_WIDTH as NODE_WIDTH,
  READONLY_TOPOLOGY_X6_PADDING_X as PADDING_X,
  READONLY_TOPOLOGY_X6_PADDING_Y as PADDING_Y,
  readReadonlyTopologyGraphJsonViewerSize,
  resizeReadonlyTopologyGraph,
  READONLY_TOPOLOGY_X6_WIDTH as WIDTH,
} from "./readonly-topology-x6-helpers";
import type {
  ReadonlyTopologyNode,
  ReadonlyTopologyX6GraphJsonViewerProps,
  ReadonlyTopologyX6ViewerProps,
} from "./readonly-topology-x6-types";

export type {
  ReadonlyTopologyEdge,
  ReadonlyTopologyLane,
  ReadonlyTopologyNode,
} from "./readonly-topology-x6-types";

export function ReadonlyTopologyX6Viewer({
  ariaLabel = "readonly topology viewer",
  lanes,
  nodes,
  edges,
  footer,
  minHeight = 280,
  onNodeClick,
}: ReadonlyTopologyX6ViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const onNodeClickRef = useRef(onNodeClick);

  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  const model = useMemo(() => {
    const normalizedLanes =
      lanes.length > 0 ? lanes : [{ key: "__default__", name: "Default Network" }];
    const nodesByLane = new Map<string, ReadonlyTopologyNode[]>();
    normalizedLanes.forEach((lane) => nodesByLane.set(lane.key, []));
    const fallbackLaneKey = normalizedLanes[0]?.key || "__default__";
    nodes.forEach((node) => {
      const laneKey = nodesByLane.has(node.laneKey) ? node.laneKey : fallbackLaneKey;
      nodesByLane.get(laneKey)?.push(node);
    });

    const laneCenters = new Map<string, number>();
    normalizedLanes.forEach((lane, index) => {
      laneCenters.set(lane.key, PADDING_Y + index * LANE_HEIGHT + LANE_HEIGHT / 2);
    });

    const nodePositions = new Map<string, { x: number; y: number }>();
    normalizedLanes.forEach((lane) => {
      const laneNodes = nodesByLane.get(lane.key) || [];
      const spacing =
        laneNodes.length > 1 ? (WIDTH - PADDING_X * 2 - NODE_WIDTH) / (laneNodes.length - 1) : 0;
      laneNodes.forEach((node, index) => {
        nodePositions.set(node.key, {
          x:
            PADDING_X +
            (laneNodes.length === 1 ? (WIDTH - PADDING_X * 2 - NODE_WIDTH) / 2 : spacing * index),
          y: (laneCenters.get(lane.key) || 0) - NODE_HEIGHT / 2,
        });
      });
    });

    return {
      lanes: normalizedLanes,
      nodePositions,
      height: PADDING_Y * 2 + normalizedLanes.length * LANE_HEIGHT,
    };
  }, [lanes, nodes]);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const container = containerRef.current;
    container.innerHTML = "";

    const graph = new Graph({
      container,
      width: container.clientWidth || WIDTH,
      height: model.height,
      panning: true,
      mousewheel: {
        enabled: true,
        modifiers: ["ctrl", "meta"],
        factor: 1.1,
        minScale: 0.5,
        maxScale: 1.6,
      },
      interacting: {
        nodeMovable: false,
        edgeMovable: false,
        vertexMovable: false,
        vertexAddable: false,
        vertexDeletable: false,
        arrowheadMovable: false,
        edgeLabelMovable: false,
      },
      connecting: {
        allowBlank: false,
        allowNode: false,
        allowEdge: false,
        allowPort: false,
        allowLoop: false,
      },
      background: {
        color: "#f8fafc",
      },
      grid: false,
    });

    model.lanes.forEach((lane, index) => {
      const top = PADDING_Y + index * LANE_HEIGHT + 4;
      graph.addNode(
        new Shape.Rect({
          x: 12,
          y: top,
          width: WIDTH - 24,
          height: LANE_HEIGHT - 8,
          attrs: {
            body: {
              fill: index % 2 === 0 ? "rgba(255,255,255,0.92)" : "rgba(241,245,249,0.92)",
              stroke: "rgba(148,163,184,0.28)",
              strokeDasharray: "4 4",
              rx: 12,
              ry: 12,
            },
            label: {
              text: lane.name,
              fill: "#475569",
              fontSize: 12,
              fontWeight: 600,
              refX: 20,
              refY: 18,
              textAnchor: "start",
              textVerticalAnchor: "middle",
            },
          },
          zIndex: 0,
        }),
      );
      if (lane.subtitle) {
        graph.addNode(
          new Shape.TextBlock({
            x: 24,
            y: top + 20,
            width: WIDTH - 60,
            height: 22,
            attrs: {
              body: {
                fill: "transparent",
                stroke: "transparent",
              },
              label: {
                text: lane.subtitle,
                fill: "#64748b",
                fontSize: 11,
                textAnchor: "start",
                textVerticalAnchor: "middle",
              },
            },
            zIndex: 1,
          }),
        );
      }
    });

    nodes.forEach((node) => {
      const position = model.nodePositions.get(node.key);
      if (!position) return;
      const accentColor = node.accentColor || "#334155";
      const nodeShape = graph.addNode(
        new Shape.Rect({
          id: node.key,
          x: position.x,
          y: position.y,
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
          attrs: {
            body: {
              fill: node.selected ? "rgba(219,234,254,0.96)" : "#ffffff",
              stroke: node.selected ? "#2563eb" : accentColor,
              strokeWidth: node.selected ? 2.5 : 1.5,
              rx: 12,
              ry: 12,
            },
            label: {
              text: buildNodeLabel(node),
              fill: "#0f172a",
              fontSize: 11,
              textAnchor: "middle",
              textVerticalAnchor: "middle",
            },
          },
          zIndex: 3,
        }),
      );
      nodeShape.setData({ kind: "topology-node", nodeKey: node.key });
      graph.addNode(
        new Shape.Circle({
          x: position.x + 10,
          y: position.y + 10,
          width: 10,
          height: 10,
          attrs: {
            body: {
              fill: accentColor,
              stroke: accentColor,
            },
          },
          zIndex: 4,
        }),
      );
      if (node.secondaryAccentColor) {
        graph.addNode(
          new Shape.Circle({
            x: position.x + NODE_WIDTH - 20,
            y: position.y + 10,
            width: 10,
            height: 10,
            attrs: {
              body: {
                fill: node.secondaryAccentColor,
                stroke: node.secondaryAccentColor,
              },
            },
            zIndex: 4,
          }),
        );
      }
    });

    edges.forEach((edge, index) => {
      if (!model.nodePositions.has(edge.sourceKey) || !model.nodePositions.has(edge.targetKey))
        return;
      graph.addEdge({
        id: `${edge.key}-${index}`,
        source: { cell: edge.sourceKey },
        target: { cell: edge.targetKey },
        attrs: {
          line: {
            stroke: "rgba(71,85,105,0.58)",
            strokeWidth: 2,
            targetMarker: null,
          },
        },
        connector: {
          name: "smooth",
        },
        router: {
          name: "normal",
        },
        zIndex: 2,
      });
    });

    graph.on("node:click", ({ node }: { node: { getData: () => unknown } }) => {
      const handleNodeClick = onNodeClickRef.current;
      if (!handleNodeClick) return;
      const data = (node.getData() as { kind?: string; nodeKey?: string } | null) || {};
      if (data.kind === "topology-node" && data.nodeKey) {
        handleNodeClick(data.nodeKey);
      }
    });

    resizeReadonlyTopologyGraph(graph, container, model.height);
    fitReadonlyTopologyGraph(graph, 16);

    const resizeObserver = new ResizeObserver(() => {
      resizeReadonlyTopologyGraph(graph, container, model.height);
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      graph.dispose();
    };
  }, [edges, model, nodes]);

  return (
    <div
      style={{
        borderRadius: 12,
        border: "1px solid rgba(148,163,184,0.18)",
        background: "linear-gradient(180deg, rgba(248,250,252,0.96), rgba(241,245,249,0.96))",
        overflow: "hidden",
      }}
    >
      <div
        ref={containerRef}
        style={{ width: "100%", height: model.height, minHeight }}
        role="img"
        aria-label={ariaLabel}
      />
      {footer ? <div style={{ padding: "8px 12px 12px" }}>{footer}</div> : null}
    </div>
  );
}

export function ReadonlyTopologyX6GraphJsonViewer({
  ariaLabel = "readonly topology graph viewer",
  graphJson,
  accessPointNodeKeys,
  enabledAccessPointNodeKeys,
  footer,
  minHeight = 280,
}: ReadonlyTopologyX6GraphJsonViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const assetApiBaseUrl = useMemo(() => getAssetApiBaseUrl(), []);
  const previewJson = useMemo(
    () =>
      transformGraphJsonForPreview(graphJson, {
        accessPointNodeKeys,
        enabledAccessPointNodeKeys,
        assetApiBaseUrl,
      }),
    [accessPointNodeKeys, assetApiBaseUrl, enabledAccessPointNodeKeys, graphJson],
  );

  useEffect(() => {
    if (!containerRef.current || !previewJson) return undefined;
    const container = containerRef.current;
    container.innerHTML = "";
    ensurePreviewNodeRegistry();
    const initialSize = readReadonlyTopologyGraphJsonViewerSize(container, minHeight);

    const graph = new Graph({
      container,
      width: initialSize.width,
      height: initialSize.height,
      interacting: false,
      panning: true,
      mousewheel: { enabled: true, modifiers: ["ctrl", "meta"] },
      background: { color: "transparent" },
      grid: { size: 20, visible: false },
    });

    try {
      if (typeof graph.fromJSON === "function") {
        graph.fromJSON(previewJson);
      } else {
        const model = (graph as { model?: { fromJSON?: (value: unknown) => void } }).model;
        model?.fromJSON?.(previewJson);
      }
    } catch {
      graph.dispose();
      return undefined;
    }

    resizeReadonlyTopologyGraph(graph, container, minHeight);
    fitReadonlyTopologyGraph(graph, 24);

    const resizeObserver = new ResizeObserver(() => {
      resizeReadonlyTopologyGraph(graph, container, minHeight);
    });
    resizeObserver.observe(container);
    if (container.parentElement) {
      resizeObserver.observe(container.parentElement);
    }
    return () => {
      resizeObserver.disconnect();
      graph.dispose();
    };
  }, [minHeight, previewJson]);

  return (
    <div
      style={{
        borderRadius: 12,
        border: "1px solid rgba(148,163,184,0.18)",
        background: "linear-gradient(180deg, rgba(248,250,252,0.96), rgba(241,245,249,0.96))",
        overflow: "hidden",
      }}
    >
      <div
        ref={containerRef}
        style={buildReadonlyTopologyGraphJsonViewerContainerStyle(minHeight)}
        role="img"
        aria-label={ariaLabel}
      />
      {footer ? <div style={{ padding: "8px 12px 12px" }}>{footer}</div> : null}
    </div>
  );
}
